import React, { useEffect, useRef } from 'react';
import { Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { bindNaturalsTouchShellClass } from './shared/bindEnterpriseTouchShell';
import { resetNaturalsSalonLuxScroll } from './shared/resetEnterpriseSalonLuxScroll';
import ModernDialogHost from './admin-shared/ModernDialogHost';
import { BEAUTY_PRODUCTS as NATURALS_BEAUTY_PRODUCTS } from '../beauty-shared/config/saasModules';
import {
  MakeupHairLegacyRedirect,
  EnterpriseMakeupHairLegacyRedirect,
  EnterpriseMakeupToLooksRedirect,
} from '../beauty-shared/components/BeautyLegacyRedirects';
import NaturalsSalonStyles from './salon/EnterpriseSalonStyles';

import { SkinSessionProvider as NaturalsSalonSessionProvider } from './salon/context/SkinSessionContext';
import { SkinAnalysisPlanProvider as NaturalsSalonPlanProvider } from './salon/context/SkinAnalysisPlanContext';
import NaturalsSalonFlowSelectPage from './salon/pages/FlowSelectPage';
import NaturalsSalonTokenEntryPage from './salon/pages/SalonTokenEntryPage';
import NaturalsFaceIdentityPage from './face-identity/pages/FaceIdentityPage';
import NaturalsFaceIdentityWelcomePage from './face-identity/pages/FaceIdentityWelcomePage';
import PassportPendingRegistrationPage from './face-identity/pages/PassportPendingRegistrationPage';
import NaturalsFaceIdentityLoginPage from './face-identity/pages/FaceIdentityLoginPage';
import RequireFranchiseFlowAuth from './shared/RequireFranchiseFlowAuth';
import NaturalsSalonFaceDetectionPage from './salon/pages/FaceDetectionPage';
import NaturalsSalonSkinMacroCapturePage from './salon/pages/SkinMacroCapturePage';
import NaturalsSalonAISkinAnalysisPage from './salon/pages/AISkinAnalysisPage';
import NaturalsSalonSkinRoutinePage from './salon/pages/SkinRoutinePage';
import NaturalsSalonProductRecommendationsPage from './salon/pages/ProductRecommendationsPage';
import NaturalsSalonTreatmentRecommendationsPage from './salon/pages/TreatmentRecommendationsPage';
import NaturalsSalonAppointmentBookingPage from './salon/pages/AppointmentBookingPage';
import NaturalsSalonResultPage from './salon/pages/SalonResultPage';
import NaturalsSalonScanPage from './salon/pages/SalonScanPage';
import NaturalsSalonRegisterPage from './salon/pages/SalonRegisterPage';
import NaturalsSalonWelcomePage from './salon/pages/SalonWelcomePage';
import NaturalsSalonStartChoicePage from './salon/pages/SalonStartChoicePage';
import NaturalsSalonVisitIntentPage from './salon/pages/VisitIntentPage';
import NaturalsSalonGuestVisitIntentPage from './salon/pages/GuestVisitIntentPage';
import NaturalsSalonGuestConfirmCheckinPage from './salon/pages/GuestConfirmCheckinPage';
import NaturalsSalonStylistGuestPickPage from './salon/pages/StylistGuestPickPage';
import NaturalsSalonStylistCustomerFlowPage from './salon/pages/StylistCustomerFlowPage';
import NaturalsSalonServiceSessionPage from './salon/pages/ServiceSessionPage';
import NaturalsSalonServiceCatalogPage from './salon/pages/ServiceCatalogPage';
import NaturalsSalonServiceLobbyPage from './salon/pages/ServiceLobbyPage';
import NaturalsSalonServiceWorkPage from './salon/pages/ServiceWorkPage';
import NaturalsSalonCustomerCodePage from './salon/pages/SalonCustomerCodePage';
import NaturalsFaceIdentityMobileLookupPage from './face-identity/pages/FaceIdentityMobileLookupPage';
import NaturalsFaceIdentityGuestEntryPage from './face-identity/pages/FaceIdentityGuestEntryPage';
import RequireGuestFlowAuth from './shared/RequireGuestFlowAuth';
import NaturalsSalonLandingBackground, {
  KIOSK_PAGE_KEYS as NATURALS_KIOSK_PAGE_KEYS,
} from './salon/components/SalonLandingBackground';
import { PageBackgroundSync as NaturalsSalonPageBackgroundSync } from './salon/components/PageBackgroundSync';
import PageFooter from './salon/components/PageFooter';
import EngagementApp from './engagement/EngagementApp';
import NyafazEngagementApp from './nyafaz-engagement/NyafazEngagementApp';
import DemoPosApp from './demo-pos/DemoPosApp';
import NaturalsVideoFlowLayout from './video/VideoFlowLayout';
import NaturalsVideoHomePage from './video/pages/VideoHome';
import NaturalsVideoServiceSelectPage from './video/pages/ServiceSelect';
import NaturalsVideoCaptureFlowPage from './video/pages/CaptureFlow';
import NaturalsVideoCreatingReelPage from './video/pages/CreatingReel';
import NaturalsVideoReadinessPage from './video/pages/Readiness';
import NaturalsVideoPreviewPage from './video/pages/Preview';
import NaturalsVideoCompressPage from './video/pages/CompressVideo';
import NaturalsMakeupHairAuthLayout from './makeuphair/components/MakeupHairAuthLayout';
import NaturalsMakeupHairCameraCapturePage from './makeuphair/pages/CameraCapturePage';
import NaturalsMakeupHairCapturePreviewPage from './makeuphair/pages/CapturePreviewPage';
import LooksMobileUploadPage from './looks/pages/LooksMobileUploadPage';
import HairStylingStudio from './hair-styling/pages/HairStylingStudio';
import HairColoringStudio from './hair-coloring/pages/HairColoringStudio';
import HairFluxPromptPage from './hair-flux-prompt/pages/HairFluxPromptPage';
import MakeupFluxPromptV3Page from './makeup-flux-prompt-v3/pages/MakeupFluxPromptV3Page';
import MakeupStudioV3Page from './makeup-studio-test/pages/MakeupStudioV3Page';
import ProductPhotoCapturePage from './partner/pages/ProductPhotoCapturePage';
import NaturalsWorkspaceProductPhotoCapturePage from './workspace/pages/ProductPhotoCapturePage';
import PresentationLoginPage from './presentation/PresentationLoginPage';
import HairFlowLayout from '../hair-analyzer/src/hair/HairFlowLayout';
import { renderHairAnalyzerRoutes } from '../hair-analyzer/src/hair/renderHairAnalyzerRoutes';
import PresentationHubPage from './presentation/PresentationHubPage';
function NaturalsSalonLegacyEntryRedirect() {
  return <Navigate to="/naturals/salon/service-lobby" replace />;
}

function FaceIdentityToPassportRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/naturals\/face-identity\/?/, '');
  let tail = rest || 'scan';
  if (tail === 'login') tail = 'login';
  else if (!tail) tail = 'scan';
  return <Navigate to={`/naturals/passport/${tail}${location.search || ''}`} replace state={location.state} />;
}

function NaturalsLegacyVideoRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/naturals\/video\/?/, '');
  const target = rest ? `/naturals/reels/${rest}` : '/naturals/reels';
  return <Navigate to={`${target}${location.search || ''}`} replace />;
}

function NaturalsEngagementLegacyRedirect({ base, from }) {
  const location = useLocation();
  const rest = location.pathname.replace(new RegExp(`^${from}/?`), '');
  const target = rest ? `${base.replace(/\/+$/, '')}/${rest}` : base.replace(/\/+$/, '');
  return <Navigate to={`${target}${location.search || ''}`} replace />;
}

function NaturalsSalonFlowLayout() {
  useEffect(() => bindNaturalsTouchShellClass(), []);
  const location = useLocation();
  const luxContentRef = useRef(null);
  const isFaceIdentity =
    location.pathname.startsWith('/naturals/face-identity') ||
    location.pathname.startsWith('/naturals/passport') ||
    location.pathname.startsWith('/naturals/guest-checkin');
  const isGuestCheckinShell =
    location.pathname.startsWith('/naturals/guest-checkin') ||
    location.pathname.startsWith('/naturals/passport/guest') ||
    location.pathname.startsWith('/naturals/passport/entry');
  const hideFooter =
    location.pathname.startsWith('/naturals/guest-checkin') ||
    location.pathname.startsWith('/naturals/passport/guest') ||
    location.pathname.startsWith('/naturals/passport/entry');
  const landingPageKey = isFaceIdentity
    ? NATURALS_KIOSK_PAGE_KEYS.FACE_IDENTITY
    : NATURALS_KIOSK_PAGE_KEYS.SALON_ENTRY;

  useEffect(() => {
    const prevRestoration = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const reset = () => resetNaturalsSalonLuxScroll(luxContentRef.current);
    reset();
    const raf = requestAnimationFrame(reset);
    const t0 = window.setTimeout(reset, 0);
    const t1 = window.setTimeout(reset, 100);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = prevRestoration || 'auto';
      }
    };
  }, [location.key]);

  return (
    <NaturalsSalonSessionProvider>
      <NaturalsSalonPlanProvider>
        <NaturalsSalonStyles />
        <NaturalsSalonPageBackgroundSync />
        <div className="naturals-salon-lux fi-lux-theme">
          <NaturalsSalonLandingBackground pageKey={landingPageKey} source="pageSetup" />
          <div
            ref={luxContentRef}
            className={`naturals-salon-lux__content${
              isGuestCheckinShell ? ' naturals-salon-lux__content--guest-checkin' : ''
            }`}
          >
            <Outlet />
            {!hideFooter ? <PageFooter /> : null}
          </div>
          <ModernDialogHost />
          <div id="naturals-salon-modal-root" />
        </div>
      </NaturalsSalonPlanProvider>
    </NaturalsSalonSessionProvider>
  );
}

export function renderNaturalsAppRoutes({ naturalsDedicatedAdmin = false } = {}) {
  return (
    <>
      <Route path="/naturals/SalonResultPage" element={<NaturalsSalonResultPage />} />
      <Route path="/naturals/salon/result" element={<NaturalsSalonResultPage />} />
      <Route path="/naturals/salon/*" element={<Navigate to="/naturals/salon" replace />} />
      <Route path="/makeup/*" element={<Navigate to="/naturals/makeup-studio-test/capture" replace />} />
      <Route path="/hair-coloring/*" element={<Navigate to="/naturals/hair-coloring/capture" replace />} />
      <Route path="/haircut/*" element={<Navigate to="/naturals/hair-styling/capture" replace />} />
      <Route
      path="/makeup-hair/*"
      element={<MakeupHairLegacyRedirect toBase="/naturals/makeup-studio-test" />}
      />
      <Route path="/hairstyle/*" element={<Navigate to="/naturals/hair-styling/capture" replace />} />
      <Route path="/naturals/looks/mobile-upload/:token" element={<LooksMobileUploadPage />} />
      <Route path="/naturals/looks/*" element={<Navigate to="/naturals/makeup-studio-test/capture" replace />} />
      <Route path="/naturals/partner/product-photo-capture/:token" element={<ProductPhotoCapturePage />} />
      <Route
        path="/naturals/workspace/product-photo-capture/:token"
        element={<NaturalsWorkspaceProductPhotoCapturePage />}
      />
      <Route path="/naturals/makeup/login" element={<Navigate to="/naturals/salon/login" replace />} />
      <Route path="/naturals/makeup/*" element={<EnterpriseMakeupToLooksRedirect toBase="/naturals/makeup-studio-test" />} />
      <Route path="/test/hair-coloring" element={<Navigate to="/naturals/hair-coloring/capture" replace />} />
      <Route path="/test/beauty" element={<Navigate to="/naturals/salon/test" replace />} />
      <Route path="/naturals/makeup-flux-lab/*" element={<Navigate to="/naturals/makeup-studio-test/capture" replace />} />
      <Route path="/naturals/hair-flux-prompt/login" element={<Navigate to="/naturals/salon/login" replace />} />
      <Route
      path="/naturals/hair-flux-prompt"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/salon/login">
      <HairFluxPromptPage />
      </RequireFranchiseFlowAuth>
      }
      />
      <Route
      path="/naturals/makeup-flux-prompt-v3/login"
      element={<Navigate to="/naturals/salon/login" replace />}
      />
      <Route
      path="/naturals/makeup-flux-prompt-v3/*"
      element={<Navigate to="/naturals/makeup-studio-test/studio" replace />}
      />
      <Route path="/naturals/makeup-studio-test/login" element={<Navigate to="/naturals/salon/login" replace />} />
      <Route
      path="/naturals/makeup-studio-test"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/salon/login">
      <NaturalsMakeupHairAuthLayout product={NATURALS_BEAUTY_PRODUCTS.makeupStudioTest} />
      </RequireFranchiseFlowAuth>
      }
      >
      <Route index element={<Navigate to="/naturals/makeup-studio-test/capture" replace />} />
      <Route path="capture" element={<NaturalsMakeupHairCameraCapturePage />} />
      <Route path="capture/preview/:mode" element={<NaturalsMakeupHairCapturePreviewPage />} />
      <Route path="studio" element={<MakeupStudioV3Page />} />
      </Route>
      <Route path="/naturals/hair-styling/login" element={<NaturalsFaceIdentityLoginPage variant="salon" />} />
      <Route
      path="/naturals/hair-styling"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/hair-styling/login">
      <NaturalsMakeupHairAuthLayout product={NATURALS_BEAUTY_PRODUCTS.hairStyling} />
      </RequireFranchiseFlowAuth>
      }
      >
      <Route index element={<Navigate to="/naturals/hair-styling/capture" replace />} />
      <Route path="capture" element={<NaturalsMakeupHairCameraCapturePage />} />
      <Route path="capture/preview/:mode" element={<NaturalsMakeupHairCapturePreviewPage />} />
      <Route path="studio" element={<HairStylingStudio forcedFocus="style" />} />
      </Route>
      <Route path="/naturals/hair-coloring/login" element={<NaturalsFaceIdentityLoginPage variant="salon" />} />
      <Route
      path="/naturals/hair-coloring"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/hair-coloring/login">
      <NaturalsMakeupHairAuthLayout product={NATURALS_BEAUTY_PRODUCTS.hairColoring} />
      </RequireFranchiseFlowAuth>
      }
      >
      <Route index element={<Navigate to="/naturals/hair-coloring/capture" replace />} />
      <Route path="capture" element={<NaturalsMakeupHairCameraCapturePage />} />
      <Route path="capture/preview/:mode" element={<NaturalsMakeupHairCapturePreviewPage />} />
      <Route path="studio" element={<HairColoringStudio />} />
      </Route>
      <Route path="/naturals/hair-color/login" element={<Navigate to="/naturals/hair-coloring/login" replace />} />
      <Route path="/naturals/hair-color/*" element={<Navigate to="/naturals/hair-coloring/capture" replace />} />
      <Route path="/naturals/hair-flux-color-prompt/*" element={<Navigate to="/naturals/salon/test" replace />} />
      <Route path="/naturals/hair-coloring-test/*" element={<Navigate to="/naturals/hair-coloring/capture" replace />} />
      <Route path="/naturals/makeuphair/login" element={<Navigate to="/naturals/looks/login" replace />} />
      <Route path="/naturals/makeuphair/*" element={<EnterpriseMakeupHairLegacyRedirect toBase="/naturals/makeup-studio-test" />} />
      <Route path="/naturals/haircut/*" element={<Navigate to="/naturals/salon/test" replace />} />
      <Route path="/naturals/salon/login" element={<NaturalsFaceIdentityLoginPage variant="salon" />} />
      <Route path="/naturals/reels/login" element={<NaturalsFaceIdentityLoginPage variant="reels" />} />
      <Route path="/naturals/nyafaz-reels/login" element={<NaturalsFaceIdentityLoginPage variant="reels" />} />
      <Route path="/naturals/presentation/login" element={<PresentationLoginPage />} />
      {/* Naturals customer engagement */}
      <Route path="/naturals/engagement/*" element={<EngagementApp />} />
      <Route path="/naturals/engagement/" element={<EngagementApp />} />
      <Route path="/naturals/engagement" element={<EngagementApp />} />
      <Route
        path="/naturals/demo-engagement/*"
        element={<NaturalsEngagementLegacyRedirect base="/naturals/engagement" from="/naturals/demo-engagement" />}
      />
      <Route
        path="/naturals/demo-engagement"
        element={<Navigate to="/naturals/engagement" replace />}
      />
      {/* Nyafaz demo engagement — same backend as Naturals, Nyafaz branding. */}
      <Route path="/naturals/nyafaz-engagement/*" element={<NyafazEngagementApp />} />
      <Route path="/naturals/nyafaz-engagement/" element={<NyafazEngagementApp />} />
      <Route path="/naturals/nyafaz-engagement" element={<NyafazEngagementApp />} />
      {/* Naturals demo POS */}
      <Route path="/naturals/demo-pos/*" element={<DemoPosApp />} />
      <Route path="/hair" element={<Navigate to="/naturals/hair" replace />} />
      <Route path="/naturals/hair" element={<HairFlowLayout />}>
        {renderHairAnalyzerRoutes()}
      </Route>
      <Route
      path="/naturals/presentation"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/presentation/login">
      <NaturalsSalonSessionProvider>
      <NaturalsSalonPlanProvider>
      <NaturalsSalonPageBackgroundSync />
      <PresentationHubPage />
      </NaturalsSalonPlanProvider>
      </NaturalsSalonSessionProvider>
      </RequireFranchiseFlowAuth>
      }
      />
      <Route
      path="/naturals/reels"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/reels/login">
      <NaturalsVideoFlowLayout />
      </RequireFranchiseFlowAuth>
      }
      >
      <Route index element={<NaturalsVideoHomePage />} />
      <Route path="new" element={<NaturalsVideoServiceSelectPage />} />
      <Route path="compress" element={<NaturalsVideoCompressPage />} />
      <Route path="capture/:sessionId" element={<NaturalsVideoCaptureFlowPage />} />
      <Route path="upload/:sessionId" element={<NaturalsVideoCreatingReelPage />} />
      <Route path="ready/:sessionId" element={<NaturalsVideoReadinessPage />} />
      <Route path="rendering/:sessionId" element={<NaturalsVideoCreatingReelPage />} />
      <Route path="preview/:sessionId" element={<NaturalsVideoPreviewPage />} />
      </Route>
      {/* Nyafaz Reels — same capture UI, FFmpeg render API (port 5120). */}
      <Route
      path="/naturals/nyafaz-reels"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/nyafaz-reels/login">
      <NaturalsVideoFlowLayout />
      </RequireFranchiseFlowAuth>
      }
      >
      <Route index element={<NaturalsVideoHomePage />} />
      <Route path="new" element={<NaturalsVideoServiceSelectPage />} />
      <Route path="compress" element={<NaturalsVideoCompressPage />} />
      <Route path="capture/:sessionId" element={<NaturalsVideoCaptureFlowPage />} />
      <Route path="upload/:sessionId" element={<NaturalsVideoCreatingReelPage />} />
      <Route path="ready/:sessionId" element={<NaturalsVideoReadinessPage />} />
      <Route path="rendering/:sessionId" element={<NaturalsVideoCreatingReelPage />} />
      <Route path="preview/:sessionId" element={<NaturalsVideoPreviewPage />} />
      </Route>
      {/* Legacy /naturals/video → dedicated reels URL */}
      <Route path="/naturals/video/*" element={<NaturalsLegacyVideoRedirect />} />
      <Route path="/naturals/video" element={<NaturalsLegacyVideoRedirect />} />
      <Route
      path="/naturals/salon"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/salon/login">
      <NaturalsSalonFlowLayout />
      </RequireFranchiseFlowAuth>
      }
      >
      <Route index element={<NaturalsSalonTokenEntryPage />} />
      <Route path="start" element={<NaturalsSalonStartChoicePage />} />
      <Route path="customer-code" element={<NaturalsSalonCustomerCodePage />} />
      <Route
      path="mobile"
      element={<NaturalsFaceIdentityMobileLookupPage variant="salon" />}
      />
      <Route path="hub" element={<NaturalsSalonFlowSelectPage />} />
      <Route path="test" element={<NaturalsSalonFlowSelectPage testMode />} />
      <Route path="landing" element={<NaturalsSalonLegacyEntryRedirect />} />
      <Route path="user-info" element={<NaturalsSalonLegacyEntryRedirect />} />
      <Route path="scan" element={<NaturalsSalonScanPage />} />
      <Route path="register" element={<NaturalsSalonRegisterPage />} />
      <Route path="welcome" element={<NaturalsSalonWelcomePage />} />
      <Route path="visit-intent" element={<NaturalsSalonVisitIntentPage />} />
      <Route path="guest-intent" element={<NaturalsSalonGuestVisitIntentPage />} />
      <Route path="guest-confirm" element={<NaturalsSalonGuestConfirmCheckinPage />} />
      <Route path="stylist-checkin" element={<NaturalsSalonStylistCustomerFlowPage />} />
      <Route path="stylist-guest" element={<NaturalsSalonStylistGuestPickPage />} />
      <Route path="service-lobby" element={<NaturalsSalonServiceLobbyPage />} />
      <Route path="service-session" element={<NaturalsSalonServiceSessionPage />} />
      <Route path="service-catalog" element={<NaturalsSalonServiceCatalogPage />} />
      <Route path="service-work" element={<NaturalsSalonServiceWorkPage />} />
      <Route path="capture" element={<NaturalsSalonFaceDetectionPage />} />
      <Route path="skin-macro" element={<NaturalsSalonSkinMacroCapturePage />} />
      <Route path="skin-analysis" element={<NaturalsSalonAISkinAnalysisPage />} />
      <Route path="skin-routine" element={<NaturalsSalonSkinRoutinePage />} />
      <Route path="product-recommendations" element={<NaturalsSalonProductRecommendationsPage />} />
      <Route path="treatment-recommendations" element={<NaturalsSalonTreatmentRecommendationsPage />} />
      <Route path="appointment" element={<NaturalsSalonAppointmentBookingPage />} />
      </Route>
      {/* Legacy Face Identity URLs → Passport */}
      <Route path="/naturals/face-identity/*" element={<FaceIdentityToPassportRedirect />} />
      <Route path="/naturals/face-identity" element={<FaceIdentityToPassportRedirect />} />
      {/* Passport clone — same Face Identity flow at a separate URL */}
      <Route path="/naturals/passport/login" element={<NaturalsFaceIdentityLoginPage variant="passport" />} />
      <Route path="/naturals/passport/entry" element={<NaturalsFaceIdentityGuestEntryPage />} />
      <Route
      path="/naturals/passport/guest"
      element={
      <RequireGuestFlowAuth entryPath="/naturals/passport/entry">
      <NaturalsSalonFlowLayout />
      </RequireGuestFlowAuth>
      }
      >
      <Route index element={<Navigate to="scan" replace />} />
      <Route path="start" element={<Navigate to="scan" replace />} />
      <Route
      path="mobile"
      element={<NaturalsFaceIdentityMobileLookupPage variant="face-identity" />}
      />
      <Route path="scan" element={<NaturalsFaceIdentityPage />} />
      <Route path="welcome" element={<NaturalsFaceIdentityWelcomePage />} />
      <Route path="pending-registration" element={<PassportPendingRegistrationPage />} />
      <Route path="register" element={<Navigate to="../scan" replace />} />
      </Route>
      {/* Guest check-in QR — phone geofence + camera (no Passport start/stop gate) */}
      <Route path="/naturals/guest-checkin/entry" element={<NaturalsFaceIdentityGuestEntryPage />} />
      <Route
      path="/naturals/guest-checkin"
      element={
      <RequireGuestFlowAuth entryPath="/naturals/guest-checkin/entry">
      <NaturalsSalonFlowLayout />
      </RequireGuestFlowAuth>
      }
      >
      <Route index element={<Navigate to="scan" replace />} />
      <Route path="start" element={<Navigate to="scan" replace />} />
      <Route
      path="mobile"
      element={<NaturalsFaceIdentityMobileLookupPage variant="face-identity" />}
      />
      <Route path="scan" element={<NaturalsFaceIdentityPage />} />
      <Route path="welcome" element={<NaturalsFaceIdentityWelcomePage />} />
      <Route path="pending-registration" element={<PassportPendingRegistrationPage />} />
      <Route path="register" element={<Navigate to="scan" replace />} />
      </Route>
      <Route
      path="/naturals/passport"
      element={
      <RequireFranchiseFlowAuth loginPath="/naturals/passport/login">
      <NaturalsSalonFlowLayout />
      </RequireFranchiseFlowAuth>
      }
      >
      <Route index element={<Navigate to="scan" replace />} />
      <Route path="start" element={<Navigate to="scan" replace />} />
      <Route
      path="mobile"
      element={<NaturalsFaceIdentityMobileLookupPage variant="face-identity" />}
      />
      <Route path="scan" element={<NaturalsFaceIdentityPage />} />
      <Route path="welcome" element={<NaturalsFaceIdentityWelcomePage />} />
      <Route path="pending-registration" element={<PassportPendingRegistrationPage />} />
      <Route path="register" element={<Navigate to="scan" replace />} />
      </Route>
      <Route
      path="/naturals/salon/face-identity"
      element={
      <Navigate
      to="/naturals/salon/service-lobby"
      replace
      state={{ mode: 'salon' }}
      />
      }
      />
    </>
  );
}
